import React, {createContext, useState, useEffect, useMemo, useCallback, useRef} from "react";
import AuthenticationService from "../../services/AuthenticationService";
import Cookies from "js-cookie"
import axios from 'axios';
import {useLocation} from "react-router-dom";
import UserService from "../../services/UserService";
import ContextHelper from "../util/ContextHelper";
const UserContext = createContext(undefined);

// Prevents an unauthenticated user from being bounced back and forth to the
// Ambassador login page forever if Ambassador redirects back here without
// ever producing a valid token (e.g. local/dev environments).
const AUTH_REDIRECT_FLAG = "authRedirectAttempted";

// Set by the CloudFront/Lambda@Edge origin-response function alongside the
// (single-use, header-only) x-response-authorization header. Unlike that
// header, this is a real Set-Cookie on the actual navigation response, so
// the browser commits it before the SPA even starts running - meaning it
// works even on a brand-new browser's very first visit (a plain JS request
// made after mount, or a service worker installed after the fact, both
// arrive too late to ever see that header).
const AUTH_COOKIE_NAME = "atlas_auth_token";

// Reads (and immediately clears) the short-lived auth cookie set by the
// edge Lambda. Returns null if it isn't present (e.g. token was already
// consumed, or the edge function didn't run for this response).
const consumeAuthCookie = () => {
  const token = Cookies.get(AUTH_COOKIE_NAME);
  if (!token) {
    return null;
  }
  Cookies.remove(AUTH_COOKIE_NAME, { path: "/" });
  return token;
};


const UserContextProvider = ({ children }) => {
  const location = useLocation();
  const pathname = location.pathname
  const [loadingInitial, setLoadingInitial] = useState(true)
  const [user, setUser] = useState(undefined);
  const tokenRequestAttempted = useRef(false);
  const initialUserDetailsFetched = useRef(false);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const redirectToAmbassador = useCallback(() => {
    if (sessionStorage.getItem(AUTH_REDIRECT_FLAG)) {
      console.error("Already attempted an Ambassador auth redirect this session. Aborting to avoid a redirect loop.");
      setLoadingInitial(false)
      return;
    }
    sessionStorage.setItem(AUTH_REDIRECT_FLAG, "true")
    window.location.href = process.env.REACT_APP_ATLAS_AMBASSADOR_BASE
  }, []);

  const getUserPrivileges = useCallback((user, route) => {
    const context = ContextHelper.getContextBoundURL(route ? route : window.location.pathname, user)
    UserService.getUserPrivileges(user.username, context)
      .then(response => {
        if (!isMounted.current) {
          return;
        }
        setUser({
          authenticated: true,
          username: user.username,
          groups: user.groups,
          context: user.context,
          email: user.email,
          loginPath: user.loginPath,
          mfaEnabled: user.mfaEnabled,
          mfaVerified: user.mfaVerified,
          privileges: response.data
        })
      })
      .catch(e => {
        console.error("Failed to fetch user privileges", e)
        if (!isMounted.current) {
          return;
        }
        // Still populate a base user object so consumers (e.g. AuthenticatedRoute)
        // don't crash trying to read properties off an undefined user.
        setUser({
          authenticated: true,
          username: user.username,
          groups: user.groups,
          context: user.context,
          email: user.email,
          loginPath: user.loginPath,
          mfaEnabled: user.mfaEnabled,
          mfaVerified: user.mfaVerified,
          privileges: []
        })
      })
      .finally(() => {
        if (isMounted.current) {
          setLoadingInitial(false)
        }
        // Initialization complete (success or failure) — safe to clear the
        // redirect flag now. Keeping it set any longer would block future
        // Ambassador redirects when the token next expires.
        sessionStorage.removeItem(AUTH_REDIRECT_FLAG)
      })
  }, []);

  const getUserDetails = useCallback(() => {
    if (!AuthenticationService.isAuthenticated()) {
      setLoadingInitial(false)
      return;
    }

    const email = AuthenticationService.getEmail();
    if (email === null) {
      // Authenticated but no email claim on the token - nothing more we can do.
      setLoadingInitial(false)
      return;
    }

    UserService.getByEmail(email)
      .then(response => {
        if (!isMounted.current) {
          return;
        }
        getUserPrivileges(response.data, pathname)
      })
      .catch(e => {
        console.error("Failed to fetch user details", e)
        if (isMounted.current) {
          setLoadingInitial(false)
        }
        // getUserPrivileges never ran, so clear the flag here too.
        sessionStorage.removeItem(AUTH_REDIRECT_FLAG)
      })
  },[getUserPrivileges, pathname])

  const getTokenFromRequest = useCallback(() => {
    // Capture whether a token was present in storage BEFORE calling
    // isAuthenticated() (which removes expired tokens as a side effect).
    // This lets us tell a silent token self-extension (a token already
    // existed) apart from a genuinely first-time login (no token ever
    // existed), so post-login/MFA reset only fires for the latter (or after
    // an explicit manual logout).
    const hadStoredTokenBeforeCheck = !!AuthenticationService.getLocalToken();

    if(!AuthenticationService.isAuthenticated() && !tokenRequestAttempted.current) {
      tokenRequestAttempted.current = true;

      const applyToken = (rawToken) => {
        console.log("Storing new authentication token")
        // NOTE: AUTH_REDIRECT_FLAG is intentionally NOT cleared here.
        // It must stay set until initialization fully completes so that the
        // 401 interceptor cannot trigger another redirect while getUserDetails
        // / getUserPrivileges are still in-flight with the fresh token.
        // The header/cookie value may arrive as "Bearer <token>" - localStorage
        // should only ever hold the raw token itself, never the prefix.
        const token = rawToken && rawToken.startsWith("Bearer ")
          ? rawToken.slice("Bearer ".length)
          : rawToken;
        // storeAuthorizationToken's returned promise only resolves once any
        // triggered post-login (MFA reset) has actually completed
        // server-side. We MUST wait for it before fetching user details -
        // otherwise that fetch can race ahead of the reset and return stale
        // mfaVerified state, incorrectly skipping the MFA screen.
        AuthenticationService.storeAuthorizationToken(token, hadStoredTokenBeforeCheck)
          .catch(e => console.error("Post-login call failed", e))
          .finally(() => {
            if (isMounted.current) {
              getUserDetails()
            }
          })
      };

      // Preferred path: the edge Lambda sets a short-lived, JS-readable
      // cookie (atlas_auth_token) alongside the single-use
      // x-response-authorization header on the real navigation response.
      // Since it's a genuine Set-Cookie, the browser commits it before the
      // SPA even starts running - this works even on a brand-new browser's
      // very first visit, unlike any approach that relies on a follow-up
      // JS-initiated request or a service worker (both arrive too late to
      // ever see the original navigation response).
      const cookieToken = consumeAuthCookie();
      if (cookieToken) {
        applyToken(cookieToken)
        return;
      }

      // Fallback for environments where the edge cookie hasn't been rolled
      // out/isn't present for some reason. This direct request is a second,
      // separate request and will usually NOT see the single-use header,
      // but costs nothing to try. Do not alter its URL/params/headers -
      // that risks the edge not matching this request to the authenticated
      // session at all.
      axios.get(document.location.origin)
        .then(response => {
          if (!isMounted.current) {
            return;
          }
          const token = response.headers['x-response-authorization']
          if (token && token !== "MISSING_IN_REQUEST") {
            applyToken(token)
          } else {
            console.log("Unable to retrieve token from request")
            redirectToAmbassador()
          }
        })
        .catch(error => {
          console.error('Error fetching authorization token:', error);
          if (isMounted.current) {
            setLoadingInitial(false)
          }
        });
    } else if (AuthenticationService.isAuthenticated()) {
      // Already had a valid session on mount (e.g. page refresh / token
      // self-extension) - no new token capture or post-login needed, so
      // it's safe to fetch user details immediately here.
      getUserDetails()
    } else {
      setLoadingInitial(false)
    }
  },[getUserDetails, redirectToAmbassador])

  const logoutUser = useCallback(() => {
    // Always clear local auth state and flag that the next successful login
    // must run post-login again, independent of whether the Ambassador
    // logout redirect itself succeeds.
    AuthenticationService.markManualLogout()
    AuthenticationService.removeIdToken()

    const realm = "auth0-udi-portal-host-oauth2-filter.ambassador"
    const xsrf = Cookies.get(`ambassador_xsrf.${realm}`)
    if (xsrf) {
      let form = document.createElement('form')
      form.method = "post"
      form.action = `${process.env.REACT_APP_ATLAS_AMBASSADOR_BASE}.ambassador/oauth2/logout?realm=${realm}`
      let xsrfInput = document.createElement('input')
      xsrfInput.type = 'hidden'
      xsrfInput.name = '_xsrf'
      xsrfInput.value = xsrf
      form.appendChild(xsrfInput)
      document.body.appendChild(form)
      form.submit()
    }
  }, []);

  // Bootstrap once on mount: try to pick up a token from the request if we
  // don't already have one, then load the user's details/privileges.
  // NOTE: getTokenFromRequest() is fully responsible for deciding when to
  // fetch user details - it does so itself, correctly sequenced after any
  // post-login/MFA reset completes for a newly captured token, or
  // immediately if a valid session already existed. Do NOT add a second,
  // unconditional getUserDetails() call here - that previously raced ahead
  // of the post-login reset and fetched stale mfaVerified state, causing
  // the MFA screen to be skipped after a fresh login or logout.
  useEffect(() => {
    if (initialUserDetailsFetched.current) {
      return;
    }
    initialUserDetailsFetched.current = true;
    getTokenFromRequest()
  }, [getTokenFromRequest]);

  // Re-fetch privileges as the route/context changes (skip the very first
  // render since the bootstrap effect above already covers it).
  const isFirstPathnameRender = useRef(true);
  useEffect(() => {
    if (isFirstPathnameRender.current) {
      isFirstPathnameRender.current = false;
      return;
    }
    getUserDetails();
  }, [pathname, getUserDetails]);

  useEffect(() => {
    const interval = setInterval(() => {
      console.log("Checking if token is expired")
      if (!AuthenticationService.isAuthenticated()) {
        console.log("User is not authenticated clearing interval")
        //redirectToAmbassador()
        return clearInterval(interval)
      }
    }, 10000);
    return () => clearInterval(interval);
  }, [redirectToAmbassador]);

  const memoedValue = useMemo(
    () => ({
      user,
      logoutUser,
      loadingInitial,
    }),
    [user, logoutUser, loadingInitial]
  );
  return (
    <UserContext.Provider value={memoedValue}>
      {(!loadingInitial) && children }
    </UserContext.Provider>
  );
};

export { UserContext, UserContextProvider };
